'use client';
import useAspidaSWR from '@aspida/swr';
import type { IdTokenResult } from '@firebase/auth';
import { useContext, useEffect } from 'react';

import type { Methods } from '~/api/v1/gachas';
import { useI18nLn } from '~/hooks/store';
import { apiClient } from '~/lib/apiClient';

import type { SupportedLanguage } from '~/constants/languages';
import { convertLanguageToISO } from '~/src/services/i18n';
import { RootContext } from '~/store';
import type { Gacha } from '~/types/resources/gacha';

type UnAuthedRequestContext = {
  query: {
    lang?: string;
    provider_info: string;
  };
};

type AuthedRequestContext = {
  headers: Methods['get']['reqHeaders'];
  query?: {
    lang?: string;
  };
};

type RequrestContext = UnAuthedRequestContext | AuthedRequestContext;

type UseGachaInfo = {
  gachas: Gacha[] | undefined;
  loading: boolean;
  error: Error;
  mutate: () => void;
};

const buildRequestContext = (input: {
  i18nLn: SupportedLanguage | null;
  token?: IdTokenResult | null;
}): RequrestContext => {
  const { i18nLn, token } = input;
  const isDefaultLang = i18nLn === 'ja' || i18nLn == null;
  const requestLang = !isDefaultLang ? convertLanguageToISO(i18nLn) : null;
  const isActiveTokenExist = token != null;
  const headers: Methods['get']['reqHeaders'] = isActiveTokenExist
    ? {
        Authorization: `Bearer ${token?.token}`,
      }
    : undefined;
  const baseQuery: Methods['get']['query'] = {
    ...(requestLang != null && { lang: requestLang }),
  };
  if (isActiveTokenExist) {
    return {
      headers,
      ...(Object.keys(baseQuery).length > 0 && { query: baseQuery }),
    };
  }

  return {
    query: {
      ...baseQuery,
      provider_info: `firebase:${process.env.NEXT_PUBLIC_PROVIDER_INFO}`,
    },
  };
};

export const useGachaInfo = (): UseGachaInfo => {
  const { token } = useContext(RootContext);
  const { i18nLn } = useI18nLn();

  const { data, error, mutate, isValidating } = useAspidaSWR(
    apiClient.v1.gachas,
    {
      ...buildRequestContext({
        i18nLn,
        token,
      }),
      dedupingInterval: 1000 * 60 * 10,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    mutate();
  }, [mutate, token?.claims?.sub]);

  return {
    gachas: data?.data,
    loading: isValidating,
    error: error,
    mutate,
  };
};
